import {
    AccordionItem,
    AccordionPanel,
    NestedAccordionItem,
} from 'components/Accordion';
import {
    FilterAccordionButton as DefaultFilterAccordionButton,
    FilterAccordionNestedButton,
    FilterCheckbox,
    FilterChipItemButton,
} from 'components/FilterAccordion';
import { useEffect, useState } from 'react';
import { BiLeaf } from 'react-icons/bi';
import { FiChevronRight } from 'react-icons/fi';
import { FilterCheckboxChipMetric } from 'components/FilterChips/FilterCheckboxChip';
import { PlaceholderText } from '../Placeholders';
import { formatVolume } from 'utils/helpers';
import styled from 'styled-components';
import useFlavorGroups from 'components/DataVis/FlavorsTable/useFlavorGroups';
import { useUID } from 'react-uid';

export function FlavorFilterOption({ children, value, ...props }) {
    return (
        <FilterCheckbox
            css={({ theme, checked }) =>
                `background-color: ${checked ? theme.purple_5 : theme.gray_6};`
            }
            {...props}
        >
            {children}
        </FilterCheckbox>
    );
}

const NestedAccordionPanel = styled(AccordionPanel)`
    padding: 0 0.75rem 0.75rem;
    background-color: ${({ theme }) => theme.white};
`;

function FlavorFilterAccordionItem({
    isOpen,
    toggleOpen,
    onChange,
    appliedFilters,
    ChipAccordionPanel,
    FilterAccordionButton = DefaultFilterAccordionButton,
    Icon = BiLeaf,
    mixpanelMeta,
    mixpanelContext = 'FilterAccordionNestedItem',
    panelId,
    queryData,
    setFilter,
    label = 'Flavors',
    NestedAccordion,
    $FilterAccordionNestedItemBgColor,
}) {
    const [activeGroup, setActiveGroup] = useState(null);
    const toggleGroupOpen = (groupName) => {
        if (activeGroup === groupName) {
            const groups = Object.values(groupsQuery.data?.groupsByName || {});
            const currentIndex = groups.findIndex(
                (group) => group.name === activeGroup,
            );
            const nextIndex = (currentIndex + 1) % groups.length;
            setActiveGroup(groups[nextIndex].name);
        } else {
            setActiveGroup(groupName);
        }
    };
    const { category, country, dataSource, order_by, ascending } = queryData;
    const groupsQuery = useFlavorGroups({
        category,
        country,
        dataSource,
        order_by,
        ascending,
    });
    const stableId = useUID();
    const hasActiveFilters = !!appliedFilters?.length > 0;
    useEffect(() => {
        if (groupsQuery.data?.groupsByName && !activeGroup) {
            const firstGroup = Object.values(groupsQuery.data.groupsByName)[0]
                ?.name;
            setActiveGroup(firstGroup);
        }
    }, [groupsQuery.data]);
    return (
        <>
            <AccordionItem $isOpen={isOpen}>
                <FilterAccordionButton
                    onClick={(e) => toggleOpen()}
                    isActive={isOpen}
                    isOpen={isOpen}
                    Icon={Icon}
                    {...(isOpen && {
                        'aria-controls': `${stableId}-${panelId}`,
                    })}
                >
                    {label}
                </FilterAccordionButton>
                {!isOpen && hasActiveFilters ? (
                    <ChipAccordionPanel
                        $liMaxWidth={'100%'}
                        $bgColor={$FilterAccordionNestedItemBgColor}
                    >
                        {appliedFilters.map((value) => (
                            <FilterChipItemButton
                                onClick={() =>
                                    setFilter(
                                        appliedFilters.filter(
                                            (filterValue) =>
                                                filterValue !== value,
                                        ),
                                    )
                                }
                                $maxWidth={'100%'}
                                key={value}
                            >
                                {value.split('||')[0]} <FiChevronRight />{' '}
                                {value.split('||')[1]}
                            </FilterChipItemButton>
                        ))}
                    </ChipAccordionPanel>
                ) : null}
            </AccordionItem>
            <NestedAccordion $isActive={isOpen}>
                {isOpen && groupsQuery.data?.groupsByName
                    ? Object.values(groupsQuery.data.groupsByName).map(
                          (group) => {
                              const isOpen = group.name === activeGroup;
                              const PanelComponent = isOpen
                                  ? NestedAccordionPanel
                                  : ChipAccordionPanel;
                              const hasActiveFilters = appliedFilters?.some(
                                  (textId) =>
                                      group.children.some(
                                          ({ textId: childTextId }) =>
                                              childTextId === textId,
                                      ),
                              );
                              return (
                                  <NestedAccordionItem
                                      $isOpen={isOpen}
                                      $FilterAccordionNestedItemBgColor="white"
                                  >
                                      <FilterAccordionNestedButton
                                          onClick={(e) =>
                                              toggleGroupOpen(group.name)
                                          }
                                          isActive={isOpen}
                                          isOpen={isOpen}
                                          Icon={null}
                                          {...(isOpen && {
                                              'aria-controls': `${group.id}-${group.name}`,
                                          })}
                                      >
                                          <span>
                                              {group.name}
                                              <FilterCheckboxChipMetric>
                                                  {formatVolume(
                                                      group.data[order_by],
                                                  )}
                                              </FilterCheckboxChipMetric>
                                          </span>
                                      </FilterAccordionNestedButton>
                                      {isOpen || hasActiveFilters ? (
                                          <PanelComponent
                                              id={`${group.id}-${group.name}`}
                                          >
                                              {group.children.map((child) => {
                                                  const isChecked = Boolean(
                                                      appliedFilters?.includes(
                                                          child.textId,
                                                      ),
                                                  );
                                                  if (!isOpen && isChecked) {
                                                      return (
                                                          <FilterChipItemButton
                                                              onClick={() => {
                                                                  onChange({
                                                                      action: 'remove-value',
                                                                      option: {
                                                                          value: child.textId,
                                                                      },
                                                                  });
                                                              }}
                                                              key={child.textId}
                                                          >
                                                              {child.name}
                                                          </FilterChipItemButton>
                                                      );
                                                  } else if (!isOpen) {
                                                      return null;
                                                  }
                                                  return (
                                                      <FlavorFilterOption
                                                          key={child.textId}
                                                          checked={isChecked}
                                                          onChange={(
                                                              nextIsChecked,
                                                          ) => {
                                                              onChange({
                                                                  action: nextIsChecked
                                                                      ? 'select-option'
                                                                      : 'remove-value',
                                                                  option: {
                                                                      value: child.textId,
                                                                  },
                                                              });
                                                          }}
                                                      >
                                                          <span>
                                                              {child.name}
                                                              <FilterCheckboxChipMetric>
                                                                  {formatVolume(
                                                                      child
                                                                          .data[
                                                                          order_by
                                                                      ],
                                                                  )}
                                                              </FilterCheckboxChipMetric>
                                                          </span>
                                                      </FlavorFilterOption>
                                                  );
                                              })}
                                          </PanelComponent>
                                      ) : null}
                                  </NestedAccordionItem>
                              );
                          },
                      )
                    : null}
                {isOpen && groupsQuery.isLoading ? (
                    <NestedAccordionItem>
                        <PlaceholderText />
                    </NestedAccordionItem>
                ) : null}
                {isOpen && groupsQuery.isError ? (
                    <span>Something went wrong. Unable to load flavors.</span>
                ) : null}
                {isOpen &&
                !groupsQuery.data?.topGroups?.length &&
                groupsQuery.isSuccess ? (
                    <span>No flavors to show for this category.</span>
                ) : null}
            </NestedAccordion>
        </>
    );
}

export default FlavorFilterAccordionItem;
